/**
 * "Add widgets" library drawer (screenshots/68, 74 #2): 400px, pushes the page left instead of covering it.
 * Tabs All · Your team · Shared · Charts & tables; tiles already on the page show a check. Drag a tile onto the grid,
 * or press Enter on it to add it to the first free spot. ⌘K focuses the search while the drawer is open.
 * Widgets outside the team's plan follow the team's tiles with a lock and the plan that has them (design k9qobd #1).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { OrgRole, UserGroup } from "../nav/navConfig";
import { catalogFor, libraryTabOf, sizesText, type CatalogEntry, type LibraryTab } from "./catalog";
import { DRAG_TYPE } from "./OverviewGrid";
import { OvIcon } from "./icons";
import { overviewStore, useOverviewLayout } from "./overviewStore";
import { LockedWidgetRows } from "../plans/LimitCards";
import { lockedWidgets } from "../plans/planLimits";

const TABS: { id: LibraryTab; label: string }[] = [{ id: "all", label: "All" }, { id: "team", label: "Your team" }, { id: "shared", label: "Shared" }, { id: "data", label: "Charts & tables" }];
const SECTION: Record<Exclude<LibraryTab, "all">, string> = { team: "For your team", shared: "Shared", data: "Charts & tables" };

export function Library({ group, role, onClose }: { group: UserGroup; role: OrgRole | null; onClose: () => void }) {
  const [tab, setTab] = useState<LibraryTab>("all");
  const [q, setQ] = useState("");
  const search = useRef<HTMLInputElement>(null);
  const onPage = useOverviewLayout((s) => s.layout.map((i) => i.type).join(","));
  const catalog = useMemo(() => catalogFor(group, role), [group, role]);
  const locked = useMemo(() => lockedWidgets(group, role), [group, role]);
  const has = useMemo(() => new Set(onPage.split(",")), [onPage]);

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); e.stopImmediatePropagation(); search.current?.focus(); }
    };
    addEventListener("keydown", k, { capture: true });
    return () => removeEventListener("keydown", k, { capture: true });
  }, []);

  const match = (x: CatalogEntry) => !q || `${x.title} ${x.description}`.toLowerCase().includes(q.toLowerCase());
  const sections = (["team", "shared", "data"] as const).filter((s) => tab === "all" || tab === s)
    .map((s) => ({ id: s, entries: catalog.filter((x) => libraryTabOf(x) === s && match(x)) })).filter((s) => s.entries.length);

  return (
    <aside className="ov-library" aria-label="Add widgets">
      <div className="ov-library__head">
        <h2>Add widgets</h2>
        <button className="ov-icon-btn" aria-label="Close" onClick={onClose}><OvIcon name="x" size={16} /></button>
      </div>
      <label className="ov-library__search">
        <OvIcon name="search" size={15} />
        <input ref={search} type="search" placeholder="Search widgets" aria-label="Search widgets" value={q} onChange={(e) => setQ(e.target.value)} />
        <kbd>⌘K</kbd>
      </label>
      <div className="ov-library__tabs" role="tablist" aria-label="Widget groups">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}{t.id !== "all" && <span className="ov-library__count">{catalog.filter((x) => libraryTabOf(x) === t.id).length}</span>}
          </button>
        ))}
      </div>
      <div className="ov-library__list" role="tabpanel">
        {sections.map((s) => (
          <section key={s.id}>
            <h3 className="ov-library__label">{SECTION[s.id]}</h3>
            <div className="ov-library__tiles">{s.entries.map((x) => <Tile key={x.type} entry={x} on={has.has(x.type)} />)}</div>
            {s.id === "team" && <LockedWidgetRows items={locked.filter((w) => !q || `${w.title} ${w.text}`.toLowerCase().includes(q.toLowerCase()))} />}
          </section>
        ))}
      </div>
    </aside>
  );
}

function Tile({ entry, on }: { entry: CatalogEntry; on: boolean }) {
  return (
    <button className={`ov-tile${on ? " is-on" : ""}`} draggable
      aria-label={`${entry.title}${on ? ", on your overview" : ""}. Add to the first free spot`}
      onClick={() => overviewStore.getState().addWidget(entry.type)}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, entry.type);
        e.dataTransfer.effectAllowed = "copy";
        document.body.dataset.ovDrag = entry.type;
        e.currentTarget.classList.add("is-dragging");
      }}
      onDragEnd={(e) => { delete document.body.dataset.ovDrag; e.currentTarget.classList.remove("is-dragging"); }}>
      <span className="ov-tile__top">
        <span className="ov-tile__icon"><OvIcon name={entry.icon} /></span>
        {on ? <OvIcon name="check" className="ov-tile__check" /> : <OvIcon name="grip-vertical" className="ov-tile__grip" />}
      </span>
      <span className="ov-tile__text">
        <span className="ov-tile__name">{entry.title}</span>
        <span className="ov-tile__sub">{on ? "On your overview" : entry.tile ?? sizesText(entry)}</span>
      </span>
    </button>
  );
}
